import { useMemo } from 'react'
import * as Diff from 'diff'
import { Message } from '../types'

interface EditConfirmationOverlayProps {
  show: boolean
  pendingEdit: Message | undefined
  onConfirm: () => void
  onCancel: () => void
}

/**
 * Overlay-style edit confirmation that appears on top of the editor content.
 * Shows changes in a track-changes style similar to Google Docs.
 */
export function EditConfirmationOverlay({
  show,
  pendingEdit,
  onConfirm,
  onCancel,
}: EditConfirmationOverlayProps) {
  // Generate inline diff view for the edit confirmation
  const inlineDiffView = useMemo(() => {
    if (!pendingEdit?.proposedEdit) return null

    const original = pendingEdit.proposedEdit.original || ''
    const modified = pendingEdit.proposedEdit.modified || ''

    // Create a word-by-word diff for inline display
    const diff = Diff.diffWords(original, modified)

    return (
      <div className='whitespace-pre-wrap font-mono text-sm leading-relaxed'>
        {diff.map((part, index) => {
          if (part.removed) {
            return (
              <span
                key={index}
                className='bg-red-100 text-red-700 line-through decoration-red-500 decoration-2 dark:bg-red-950/50 dark:text-red-400'
              >
                {part.value}
              </span>
            )
          }
          if (part.added) {
            return (
              <span
                key={index}
                className='bg-green-100 text-green-700 dark:bg-green-950/50 dark:text-green-400'
              >
                {part.value}
              </span>
            )
          }
          return (
            <span key={index} className='text-gray-700 dark:text-gray-300'>
              {part.value}
            </span>
          )
        })}
      </div>
    )
  }, [pendingEdit])

  if (!show || !pendingEdit) return null

  // Find the editor container wrapper to overlay on top of
  const editorWrapper = document.querySelector('.editor-container-wrapper')
  const editorRect = editorWrapper?.getBoundingClientRect()

  if (!editorRect) return null

  return (
    <div
      className='pointer-events-none fixed z-[100] flex items-center justify-center'
      style={{
        top: `${editorRect.top}px`,
        left: `${editorRect.left}px`,
        width: `${editorRect.width}px`,
        height: `${editorRect.height}px`,
      }}
    >
      {/* Semi-transparent overlay background with blur effect */}
      <div className='pointer-events-auto absolute inset-0 backdrop-blur-lg' />

      {/* Centered content container with naked text overlay - takes up most of the height */}
      <div className='pointer-events-auto relative z-10 mx-auto flex h-[95%] w-full max-w-2xl flex-col'>
        {/* Header - naked text overlay */}
        <div className='flex-shrink-0 px-6 pb-4 text-center'>
          <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
            Review Changes
          </h3>
        </div>

        {/* Action buttons - moved to top */}
        <div className='flex-shrink-0 px-6 pb-4'>
          <div className='flex justify-center space-x-3'>
            <button
              onClick={onCancel}
              className='rounded-lg border-2 border-gray-900 bg-transparent px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-900 hover:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 dark:border-white dark:text-white dark:hover:bg-white dark:hover:text-gray-900'
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className='rounded-lg border-2 border-amber-600 bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-600 focus:ring-offset-2 dark:border-amber-300 dark:bg-amber-600 dark:hover:bg-amber-700'
            >
              Apply Changes
            </button>
          </div>
        </div>

        {/* Diff view container - takes up most of the available height */}
        <div className='flex-1 overflow-y-auto p-4'>
          <div className='text-white'>
            <div className='mb-2 text-center'>
              <span className='text-xs font-medium text-white'>
                Track Changes View
              </span>
            </div>
            <div className='overflow-x-auto'>{inlineDiffView}</div>
          </div>
        </div>
      </div>
    </div>
  )
}
